/**
 * OSMARIS BUNDLE SELECTOR
 *
 * Set-Stufen auf der Produktseite. Stufe 1 ist das reine Produkt und laeuft unveraendert
 * durch den Theme-Code. Stufe 2 und 3 legen zusaetzlich n Exemplare des Zusatzprodukts
 * in den Warenkorb — dafuer wird der Submit in der Capture-Phase abgefangen.
 *
 * Warum Capture-Phase auf document:
 *   - laeuft vor ProductForm._onSubmit, das am <form> selbst haengt
 *   - haengt nicht am Button oder Formular und ueberlebt damit jeden ProductRerender,
 *     der den buy-buttons-Block austauscht
 *
 * Wichtig, empirisch geprueft am 30.07.2026:
 *   /cart/add.js mit items[] ist NICHT atomar. Reicht der Bestand nicht, kappt Shopify
 *   die Menge, gibt 422 zurueck UND haelt das Hinzugefuegte im Warenkorb. Der Fehlerpfad
 *   muss den Warenkorb deshalb neu lesen und die Oberflaeche aktualisieren, statt
 *   "nichts passiert" anzunehmen.
 */
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const parseJSON = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('[osmaris-bundle] Datenblock ist kein gueltiges JSON', error);
      return fallback;
    }
  };

  class OsmarisBundleSelector extends HTMLElement {
    #data = null;
    #cartQuantities = new Map();
    #tierIndex = 0;
    #mainVariantId = null;
    #colorTouched = false;
    #abort = null;

    connectedCallback() {
      this.#data = parseJSON(this.querySelector('[data-osmaris-bundle-data]'), null);
      if (!this.#data) return;

      this.#tierIndex = Number(this.getAttribute('default-tier') || 0);
      this.#mainVariantId = this.#readMainVariantId();

      // Auswahl nach einem vollen Rerender wiederherstellen.
      const stored = this.#readStored();
      if (stored) {
        if (Number.isInteger(stored.tier)) this.#tierIndex = stored.tier;
        if (stored.color) this.#selectColorInput(stored.color);
      }

      this.addEventListener('change', this.#onChange);
      document.addEventListener('variant:change', this.#onVariantChange);
      document.addEventListener('cart:change', this.#onCartChange);
      // Auch auf cart:refresh hoeren: das Theme nutzt es als Fehler-Fallback, und ohne
      // Neuladen der Mengen rechnet die Bestandslogik mit veraltetem Warenkorb weiter.
      document.addEventListener('cart:refresh', this.#onCartChange);

      this.#refreshCartQuantities().then(() => this.#render());
      this.#render();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.#onVariantChange);
      document.removeEventListener('cart:change', this.#onCartChange);
      document.removeEventListener('cart:refresh', this.#onCartChange);
      if (this.#abort) this.#abort.abort();
    }

    /* ------------------------------------------------------------------ oeffentliche API */

    get formId() {
      return this.getAttribute('form-id');
    }

    get tiers() {
      return (this.#data && this.#data.tiers) || [];
    }

    get extraQuantity() {
      const tier = this.tiers[this.#tierIndex];
      return tier ? Number(tier.extra) || 0 : 0;
    }

    get selectedColorId() {
      const input = this.querySelector('[data-osmaris-color-input]:checked');
      return input ? Number(input.value) : null;
    }

    /** Nur wenn das hier true ist, wird der Theme-Submit ersetzt. */
    get canBundle() {
      if (!this.#data) return false;
      if (this.extraQuantity <= 0) return false;
      if (!this.selectedColorId) return false;
      return this.extraQuantity <= this.#maxAddable(this.selectedColorId);
    }

    /* ---------------------------------------------------------------------- Zustand */

    #readMainVariantId() {
      const form = document.getElementById(this.formId);
      const input = form && form.querySelector('input[name="id"]');
      const fromForm = input && Number(input.value);
      if (fromForm) return fromForm;

      const first = (this.#data.mainVariants || [])[0];
      return first ? first.id : null;
    }

    #storageKey() {
      return `osmaris:bundle:${this.getAttribute('product-id')}`;
    }

    #readStored() {
      try {
        const raw = sessionStorage.getItem(this.#storageKey());
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    }

    #store() {
      try {
        sessionStorage.setItem(
          this.#storageKey(),
          JSON.stringify({ tier: this.#tierIndex, color: this.selectedColorId })
        );
      } catch (error) {
        /* Private-Mode o. Ae. — die Wiederherstellung ist Komfort, kein Muss. */
      }
    }

    #selectColorInput(variantId) {
      const input = this.querySelector(`[data-osmaris-color-input][value="${variantId}"]`);
      if (input && !input.disabled) input.checked = true;
    }

    async #refreshCartQuantities() {
      if (this.#abort) this.#abort.abort();
      this.#abort = new AbortController();

      try {
        const response = await fetch(`${ROOT}cart.js`, { signal: this.#abort.signal });
        if (!response.ok) return;
        const cart = await response.json();

        this.#cartQuantities = new Map();
        (cart.items || []).forEach((item) => {
          const current = this.#cartQuantities.get(item.variant_id) || 0;
          this.#cartQuantities.set(item.variant_id, current + item.quantity);
        });
      } catch (error) {
        /* Abbruch oder Netzfehler: wir rechnen dann ohne Warenkorbmengen weiter. */
      }
    }

    #addonVariant(variantId) {
      return (this.#data.addonVariants || []).find((variant) => variant.id === Number(variantId)) || null;
    }

    /**
     * Wie viele Stueck lassen sich jetzt noch hinzufuegen?
     * Nur bei "deny" plus aktiver Bestandsfuehrung gibt es eine Obergrenze —
     * bei "continue" darf inventoryQuantity NICHT als Limit gelesen werden.
     */
    #maxAddable(variantId) {
      const variant = this.#addonVariant(variantId);
      if (!variant || !variant.available) return 0;

      const managed = variant.inventoryManaged && variant.inventoryManaged !== 'null';
      if (!managed || variant.inventoryPolicy !== 'deny') return Number.POSITIVE_INFINITY;

      const inCart = this.#cartQuantities.get(variant.id) || 0;
      return Math.max(0, Number(variant.inventoryQuantity || 0) - inCart);
    }

    /* ---------------------------------------------------------------------- Rendering */

    #render() {
      if (!this.#data) return;

      // Nach aussen sichtbar machen, welche Hauptvariante gerade gilt — hilft beim Debuggen
      // und macht im DOM pruefbar, ob variant:change angekommen ist.
      if (this.#mainVariantId) this.setAttribute('data-main-variant', String(this.#mainVariantId));

      // Kacheln zeigen die gewaehlte Geraetefarbe.
      const mainVariant = (this.#data.mainVariants || []).find((v) => v.id === Number(this.#mainVariantId));
      if (mainVariant && mainVariant.image) {
        this.querySelectorAll('.osmaris-bundle__image').forEach((img) => {
          if (img.getAttribute('src') === mainVariant.image) return;
          img.removeAttribute('srcset');
          img.setAttribute('src', mainVariant.image);
        });
      }

      const colorId = this.selectedColorId;
      const colorVariant = colorId ? this.#addonVariant(colorId) : null;
      const addable = colorId ? this.#maxAddable(colorId) : 0;
      const anyColorAvailable = (this.#data.addonVariants || []).some((variant) => variant.available);

      this.querySelectorAll('[data-osmaris-tier]').forEach((tile) => {
        const index = Number(tile.getAttribute('data-osmaris-tier'));
        const tier = this.tiers[index];
        if (!tier) return;

        const extra = Number(tier.extra) || 0;
        const hintEl = tile.querySelector('[data-osmaris-tier-hint]');
        let disabled = false;
        let hint = '';

        if (extra > 0) {
          if (!anyColorAvailable) {
            disabled = true;
            hint = `${this.#data.addonTitle} ist gerade nicht verfügbar.`;
          } else if (!colorVariant || !colorVariant.available) {
            disabled = true;
            hint = colorVariant ? `${colorVariant.title} ist gerade aus.` : '';
          } else if (extra > addable) {
            disabled = true;
            hint =
              addable > 0
                ? `Von ${colorVariant.title} ${addable === 1 ? 'ist nur noch 1 Stück' : `sind nur noch ${addable} Stück`} da.`
                : `${colorVariant.title} ist gerade aus.`;
          }
        }

        tile.classList.toggle('is-disabled', disabled);
        tile.classList.toggle('is-selected', index === this.#tierIndex);

        const input = tile.querySelector('[data-osmaris-tier-input]');
        if (input) {
          input.disabled = disabled;
          input.checked = index === this.#tierIndex;
        }

        if (hintEl) {
          hintEl.textContent = hint;
          hintEl.hidden = hint === '';
        }

        this.#renderPrices(tile, extra, colorId);
      });

      // Ist die gewaehlte Stufe unmoeglich geworden, faellt die Auswahl auf Stufe 1 zurueck.
      const selectedTile = this.querySelector(`[data-osmaris-tier="${this.#tierIndex}"]`);
      if (selectedTile && selectedTile.classList.contains('is-disabled')) {
        this.#tierIndex = 0;
        this.#render();
        return;
      }

      this.#syncColorToDevice();
      this.#renderSummary();

      const colorsWrapper = this.querySelector('[data-osmaris-colors]');
      if (colorsWrapper) colorsWrapper.hidden = this.extraQuantity === 0;

      this.querySelectorAll('[data-osmaris-color]').forEach((label) => {
        const id = Number(label.getAttribute('data-osmaris-color'));
        label.classList.toggle('is-selected', id === colorId);
      });

      // Der Hinweis unter der Farbwahl wird in #renderSummary gesetzt — er haengt an der
      // Geraetefarbe, nicht bloss daran, ob irgendeine Farbe ausverkauft ist.

      this.#store();
    }

    /** Farbname der aktuell gewaehlten Geraetevariante. */
    #deviceTitle() {
      const v = (this.#data.mainVariants || []).find((x) => x.id === Number(this.#mainVariantId));
      return v ? v.title : '';
    }

    /**
     * Extra-Flaschen folgen der Geraetefarbe, solange der Kunde nichts anderes gewaehlt hat.
     * Ist diese Farbe ausverkauft, faellt die Auswahl auf eine verfuegbare — das wird im
     * Hinweis darunter ausgesprochen, statt es stillschweigend zu tun.
     */
    #syncColorToDevice() {
      if (this.#colorTouched) return;

      const titel = this.#deviceTitle();
      const varianten = this.#data.addonVariants || [];
      const passend = varianten.find((v) => v.title === titel);
      const ziel = passend && passend.available ? passend : varianten.find((v) => v.available);
      if (!ziel) return;

      const input = this.querySelector(`[data-osmaris-color-input][value="${ziel.id}"]`);
      if (input && !input.disabled && !input.checked) input.checked = true;
    }

    #renderSummary() {
      const titel = this.#deviceTitle();
      const extra = this.extraQuantity;
      const colorId = this.selectedColorId;
      const colorVariant = colorId ? this.#addonVariant(colorId) : null;

      const setze = (sel, wert) => {
        const el = this.querySelector(sel);
        if (el) el.textContent = wert;
      };

      setze('[data-osmaris-sum-device]', titel);
      setze('[data-osmaris-sum-included]', titel);

      const zeile = this.querySelector('[data-osmaris-sum-extra-row]');
      if (zeile) {
        zeile.hidden = extra === 0;
        setze('[data-osmaris-sum-extra-label]', `${extra} Flaschen zusätzlich`);
        setze('[data-osmaris-sum-extra]', colorVariant ? colorVariant.title : '');
      }

      const labelEl = this.querySelector('[data-osmaris-colors] .osmaris-bundle__colors-label');
      if (labelEl && labelEl.dataset.tpl === undefined) labelEl.dataset.tpl = labelEl.textContent;
      if (labelEl) labelEl.textContent = (labelEl.dataset.tpl || '').replace('{n}', String(extra));

      const note = this.querySelector('[data-osmaris-color-note]');
      if (note) {
        const passend = (this.#data.addonVariants || []).find((v) => v.title === titel);
        const abweichung = passend && !passend.available && colorVariant;
        const text = abweichung
          ? `${titel} ist bei den Flaschen gerade aus — deine ${extra} zusätzlichen Flaschen kommen in ${colorVariant.title}.`
          : '';
        note.textContent = text;
        note.hidden = text === '' || extra === 0;
      }
    }

    #renderPrices(tile, extra, colorId) {
      const prices = this.#data.prices || {};
      const fallbackColor = (this.#data.addonVariants || [])[0];
      const priceColorId = colorId || (fallbackColor && fallbackColor.id);
      const entry = prices[`${this.#mainVariantId}:${extra}:${priceColorId}`];
      if (!entry) return;

      const totalEl = tile.querySelector('[data-osmaris-total]');
      const compareEl = tile.querySelector('[data-osmaris-compare]');
      const saveEl = tile.querySelector('[data-osmaris-save]');
      const volumeEl = tile.querySelector('[data-osmaris-volume]');

      if (totalEl) totalEl.textContent = entry.total;

      // Die separate Mengenrabatt-Zeile ist aus dem Markup entfernt, um Platz zu sparen.
      // Falls jemand sie wieder einbaut, wird sie hier weiterhin bedient.
      if (volumeEl) {
        const pct = Number(entry.volumePct) || 0;
        volumeEl.textContent = pct > 0 ? `inkl. ${pct} % Mengenrabatt` : '';
        volumeEl.hidden = pct <= 0;
      }

      const showCompare = this.#data.showCompare && entry.savingCents > 0;

      if (compareEl) {
        compareEl.textContent = showCompare ? entry.compare : '';
        compareEl.hidden = !showCompare;
      }

      if (saveEl) {
        // Prozent statt Betrag — der Betrag steht rechts als Preis und Streichpreis.
        // savePct bezieht sich auf genau diesen Streichpreis, nicht auf den Mengenrabatt.
        const savePct = Number(entry.savePct) || 0;
        const zeigeProzent = showCompare && savePct > 0;
        saveEl.textContent = zeigeProzent ? `Spare ${savePct} %` : '';
        saveEl.hidden = !zeigeProzent;
      }
    }

    #showError(message) {
      const el = this.querySelector('[data-osmaris-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    /* ------------------------------------------------------------------------ Events */

    #onChange = (event) => {
      const target = event.target;

      if (target.matches('[data-osmaris-tier-input]')) {
        this.#tierIndex = Number(target.value);
        this.#showError('');
        this.#render();
      }

      if (target.matches('[data-osmaris-color-input]')) {
        // Ab jetzt gilt die Wahl des Kunden, auch wenn er die Geraetefarbe wechselt.
        this.#colorTouched = true;
        this.#showError('');
        this.#render();
      }
    };

    #onVariantChange = (event) => {
      if (!event.detail || !event.detail.variant) return;

      // NICHT ueber event.detail.formId vergleichen: das Theme setzt dort this.#form.id,
      // und bei einem <form> mit <input name="id"> liefert .id wegen
      // [LegacyOverrideBuiltIns] das INPUT-ELEMENT statt der ID-Zeichenkette.
      // Genau darauf baut ProductForm.connectedCallback mit this.id.disabled = false.
      const target = event.target;
      if (!(target instanceof HTMLFormElement)) return;
      if (target.getAttribute('id') !== this.formId) return;

      this.#mainVariantId = event.detail.variant.id;
      this.#render();
    };

    #onCartChange = () => {
      this.#refreshCartQuantities().then(() => this.#render());
    };

    /* ------------------------------------------------------------------------ Submit */

    async submitBundle(form) {
      const button = form.querySelector('[type="submit"]');
      const idInput = form.querySelector('input[name="id"]');
      const mainId = Number(idInput && idInput.value) || this.#mainVariantId;
      const addonId = this.selectedColorId;
      const extra = this.extraQuantity;

      this.#showError('');

      // Sections einsammeln wie das Theme: der CartDrawer traegt hier seine ID ein.
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
      );

      // Ohne die Turbo-Bundles-Properties rabattiert die App-Function die Extra-Flaschen
      // nicht — das Bundle waere dann teurer als dieselbe Menge einzeln gekauft.
      // Verifiziert am 30.07.2026: 2 Flaschen ohne Property 63,98 EUR, mit Property 54,38 EUR.
      const addonItem = { id: addonId, quantity: extra };

      if (this.#data.volumeEnabled && this.#data.dealId) {
        addonItem.properties = {
          _turbo_bundles_deal_id: this.#data.dealId,
          _turbo_bundles_discount_type: this.#data.discountType || 'percentage',
        };
      }

      const body = { items: [{ id: mainId, quantity: 1 }, addonItem] };

      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      if (button) button.setAttribute('aria-busy', 'true');

      let addJson = null;
      let ok = false;

      try {
        const response = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });

        ok = response.ok;
        addJson = await response.json();
      } catch (error) {
        if (button) button.removeAttribute('aria-busy');
        this.#showError('Das hat nicht geklappt. Versuch es bitte noch einmal.');
        return;
      }

      // Auch im Fehlerfall: der Warenkorb kann sich veraendert haben (Shopify kappt Mengen).
      let cart = null;
      try {
        cart = await (await fetch(`${ROOT}cart.js`)).json();
        if (addJson && addJson.sections) cart.sections = addJson.sections;
      } catch (error) {
        /* ignorieren — wir dispatchen dann ohne Warenkorbdaten */
      }

      if (button) button.removeAttribute('aria-busy');

      // 'variant:add' oeffnet den Drawer. 'cart:change' aktualisiert seinen Inhalt —
      // aber nur, wenn wir das Section-HTML wirklich mitbringen: CartDrawer._onCartChanged
      // parst cart.sections[sectionId] ungeprueft und wuerde auf undefined scheitern.
      const hatSections = Boolean(cart && cart.sections && sections.length);

      if (cart) {
        form.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: (addJson && addJson.items) || [], cart },
          })
        );
      }

      if (cart && hatSections) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart },
          })
        );
      } else {
        // Ein Request mehr, dafuer garantiert korrekt: der Drawer holt sich sein HTML selbst.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      await this.#refreshCartQuantities();
      this.#render();

      if (!ok) {
        this.#showError(
          (addJson && (addJson.description || addJson.message)) ||
            'Nicht alles konnte hinzugefügt werden. Bitte prüf deinen Warenkorb.'
        );
      }
    }
  }

  window.customElements.get('osmaris-bundle-selector') ||
    window.customElements.define('osmaris-bundle-selector', OsmarisBundleSelector);

  /* -------------------------------------------------------------------- Submit-Abfang */

  if (!window.__osmarisBundleSubmitReady) {
    window.__osmarisBundleSubmitReady = true;

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!(form.getAttribute('action') || '').includes('/cart/add')) return;

        // getAttribute('id'), nicht form.id: bei einem <form> mit <input name="id">
        // liefert .id das Input-Element statt der ID-Zeichenkette.
        const formId = form.getAttribute('id');
        if (!formId) return;

        const selector = document.querySelector(
          `osmaris-bundle-selector[form-id="${CSS.escape(formId)}"]`
        );

        // Kein Selector, Basis-Stufe oder nicht lieferbar: das Theme macht wie bisher.
        if (!selector || !selector.canBundle) return;

        event.preventDefault();
        event.stopPropagation(); // ProductForm._onSubmit wird nie erreicht
        selector.submitBundle(form);
      },
      true
    );
  }
})();
